import { useRef } from "react";
import { Callout } from "ancestry-ui-react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useGenericModal } from "@/shared/components/GenericModal";
import {
    trackHelpUsageTextClick,
    trackHelpUsageTextModalClosed,
    trackHelpUsageTextModalViewed,
} from "@/shared/lib/ube";
import { useFieldContext } from "./FieldContext";
import styles from "./FieldUsage.module.scss";

// Small reusable HTML renderer
const HtmlContent = ({ html, className }: { html: string; className?: string }) => (
    <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

// Modal strategy - uses GenericModal
const ModalUsage = ({
    exampleText,
    descriptionHtml,
    viewTitle,
    fieldName,
}: {
    exampleText: string;
    descriptionHtml: string;
    viewTitle: string | undefined;
    fieldName: string | undefined;
}) => {
    const { openModal, closeModal } = useGenericModal();
    const { t } = useTranslation();

    const renderModalContent = () => (
        <>
            <HtmlContent html={descriptionHtml} />
            <button
                type="button"
                className={classNames("ancBtn silver", styles.closeButton)}
                onClick={closeModal}
                data-testid="field-usage-close-button"
            >
                {t("common.close")}
            </button>
        </>
    );

    const handleClick = () => {
        if (fieldName) trackHelpUsageTextClick(fieldName);
        openModal({
            render: renderModalContent,
            title: viewTitle,
            onOpen: () => fieldName && trackHelpUsageTextModalViewed(fieldName),
            onClose: () => fieldName && trackHelpUsageTextModalClosed(fieldName),
        });
    };

    return (
        <button type="button" className={classNames("link", styles.usageButton)} onClick={handleClick}>
            {exampleText}
        </button>
    );
};

// Callout strategy
const CalloutUsage = ({
    exampleText,
    descriptionHtml,
    fieldName,
}: {
    exampleText: string;
    descriptionHtml: string;
    fieldName: string | undefined;
}) => {
    const triggerRef = useRef<HTMLButtonElement>(null);

    const handleClick = () => {
        if (fieldName) trackHelpUsageTextClick(fieldName);
    };

    return (
        <>
            <button
                type="button"
                className={classNames("link", styles.usageButton)}
                ref={triggerRef}
                onClick={handleClick}
            >
                {exampleText}
            </button>

            <Callout trigger={triggerRef} open={false}>
                <HtmlContent html={descriptionHtml} className="fieldUsageText calloutDomContent" />
            </Callout>
        </>
    );
};

// Main component
export const FieldUsage = () => {
    const { fieldConfig } = useFieldContext();

    if (!fieldConfig.guidance) return null;

    const { exampleText, descriptionHtml, viewType, viewTitle, fieldName } = fieldConfig.guidance;

    return viewType === "Modal" ? (
        <ModalUsage
            exampleText={exampleText}
            descriptionHtml={descriptionHtml}
            viewTitle={viewTitle}
            fieldName={fieldName}
        />
    ) : (
        <CalloutUsage exampleText={exampleText} descriptionHtml={descriptionHtml} fieldName={fieldName} />
    );
};
